import React, { useMemo, useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Spinner, Alert, Button, ProgressBar, Badge } from 'react-bootstrap';
import FalconCardHeader from '../../../../../components/common/FalconCardHeader';

// Carga diferida del mapa para evitar bloqueo
const LeafletMap = React.lazy(() =>
    new Promise(resolve => {
        setTimeout(() => {
            resolve(import('./LeafletMap'));
        }, 200); // Delay para priorizar otros componentes
    })
);

// Estados de renderizado progresivo
const LOADING_STATES = {
    INITIAL: 'initial',
    PROCESSING: 'processing',
    READY: 'ready',
    RENDERED: 'rendered'
};

// Modos de carga progresiva
const LOADING_MODES = {
    SAMPLE: 'sample',      // Muestra estadística representativa
    PARTIAL: 'partial',    // Carga parcial
    FULL: 'full'          // Datos completos
};

// Configuración de chunks por modo
const CHUNK_SIZES = {
    SAMPLE: 500,          // Muestra inicial representativa
    PARTIAL: 0,        // Carga parcial optimizada
    FULL_CHUNK: 5000,     // Chunks para carga completa
    MAX_SAFE: 8000        // Límite máximo seguro
};

const AlertaUbicacion = ({ data = [], isGroup = false }) => {
    const [loadingState, setLoadingState] = useState(LOADING_STATES.INITIAL);
    const [loadingMode, setLoadingMode] = useState(LOADING_MODES.SAMPLE);
    const [showOptimizationAlert, setShowOptimizationAlert] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [showLargeDatasetAlert, setShowLargeDatasetAlert] = useState(true);


    // 🚀 FUNCIÓN: Muestreo sistemático (más uniforme que aleatorio)
    const getSystematicSample = (array, sampleSize) => {
        if (!Array.isArray(array) || array.length <= sampleSize) return array;

        const step = array.length / sampleSize;
        const sample = [];

        for (let i = 0; i < sampleSize; i++) {
            const index = Math.floor(i * step);
            if (index < array.length) {
                sample.push(array[index]);
            }
        }

        return sample;
    };

    // 🚀 FUNCIÓN: Clustering geográfico avanzado (SIN RECURSIÓN)
    const geographicClustering = (points, maxPoints) => {
        if (!Array.isArray(points) || points.length <= maxPoints) return points;

        try {
            console.log(`🗺️ Aplicando clustering geográfico: ${points.length} → ${maxPoints} puntos`);

            // Dividir el espacio en una grilla y tomar un punto representativo de cada celda
            const gridSize = Math.ceil(Math.sqrt(maxPoints));
            const clusters = new Map();

            // Encontrar bounds geográficos
            const lats = points.map(p => parseFloat(p.latitud)).filter(lat => !isNaN(lat));
            const lngs = points.map(p => parseFloat(p.longitud)).filter(lng => !isNaN(lng));

            if (lats.length === 0 || lngs.length === 0) {
                console.warn("No hay coordenadas válidas para clustering");
                return getSystematicSample(points, maxPoints); // ✅ FALLBACK SEGURO
            }

            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLng = Math.min(...lngs);
            const maxLng = Math.max(...lngs);

            const latStep = (maxLat - minLat) / gridSize || 0.001;
            const lngStep = (maxLng - minLng) / gridSize || 0.001;

            points.forEach(point => {
                const lat = parseFloat(point.latitud);
                const lng = parseFloat(point.longitud);

                if (isNaN(lat) || isNaN(lng)) return;

                const gridLat = Math.floor((lat - minLat) / latStep);
                const gridLng = Math.floor((lng - minLng) / lngStep);
                const gridKey = `${gridLat}-${gridLng}`;

                // Tomar el primer punto de cada celda
                if (!clusters.has(gridKey)) {
                    clusters.set(gridKey, {
                        ...point,
                        clusteredPoints: 1
                    });
                } else {
                    // Incrementar contador de puntos agrupados
                    const existing = clusters.get(gridKey);
                    existing.clusteredPoints = (existing.clusteredPoints || 1) + 1;
                }
            });

            const result = Array.from(clusters.values()).slice(0, maxPoints);
            console.log(`✅ Clustering completado: ${result.length} clusters creados`);
            return result;
        } catch (error) {
            console.error("Error en clustering geográfico:", error);
            return getSystematicSample(points, maxPoints); // ✅ FALLBACK SEGURO
        }
    };

    // 🚀 FUNCIÓN: Muestreo estratificado SIN RECURSIÓN
    const calculateRepresentativeSample = (points, maxPoints) => {
        if (!Array.isArray(points) || points.length <= maxPoints) return points;

        try {
            console.log(`📊 Calculando muestra representativa de ${points.length} puntos`);

            // 1. MUESTREO ESTRATIFICADO por tipo de alerta
            const stratifiedSample = [];
            const alertGroups = new Map();

            // Agrupar por tipo de alerta
            points.forEach(point => {
                const alertType = point.alerta || 'Sin clasificar';
                if (!alertGroups.has(alertType)) {
                    alertGroups.set(alertType, []);
                }
                alertGroups.get(alertType).push(point);
            });

            // Calcular proporción de muestra por grupo
            const totalGroups = alertGroups.size;
            if (totalGroups === 0) return getSystematicSample(points, maxPoints);

            const pointsPerGroup = Math.floor(maxPoints / totalGroups);
            const remainingPoints = maxPoints % totalGroups;

            let groupIndex = 0;
            alertGroups.forEach((groupPoints, alertType) => {
                const sampleSize = pointsPerGroup + (groupIndex < remainingPoints ? 1 : 0);
                const groupSample = getSystematicSample(groupPoints, sampleSize);

                stratifiedSample.push(...groupSample);
                console.log(`📍 ${alertType}: ${groupPoints.length} → ${groupSample.length} puntos`);
                groupIndex++;
            });

            console.log(`✅ Muestra estratificada: ${stratifiedSample.length} puntos de ${points.length} originales`);
            return stratifiedSample.slice(0, maxPoints);
        } catch (error) {
            console.error("Error en muestreo estratificado:", error);
            return getSystematicSample(points, maxPoints); // ✅ FALLBACK SEGURO
        }
    };

    // 🚀 OPTIMIZACIÓN CRÍTICA: Sistema de carga progresiva CORREGIDO
    const { optimizedData, isOptimized, stats, loadingOptions } = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return {
                optimizedData: [],
                isOptimized: false,
                stats: {
                    original: 0,
                    valid: 0,
                    final: 0,
                    mode: loadingMode,
                    samplingMethod: 'ninguno',
                    alertTypes: 0,
                    alertDistribution: []
                },
                loadingOptions: {
                    canLoadMore: false,
                    canLoadFull: false,
                    showSampleInfo: false,
                    totalAvailable: 0,
                    currentShowing: 0,
                    nextLoadSize: 0
                }
            };
        }

        //console.log(`🗺️ Procesando datos del mapa: ${data.length} puntos originales (modo: ${loadingMode})`);

        // Filtrar puntos válidos primero
        const validPoints = data.filter(point => {
            const lat = parseFloat(point.latitud);
            const lng = parseFloat(point.longitud);
            return !isNaN(lat) && !isNaN(lng) &&
                lat !== 0 && lng !== 0 &&
                lat >= -90 && lat <= 90 &&
                lng >= -180 && lng <= 180;
        });

        let maxPoints;
        let processedData = validPoints;
        let optimized = false;
        let samplingMethod = 'ninguno';

        // Determinar cantidad de puntos y método según el modo
        switch (loadingMode) {
            case LOADING_MODES.SAMPLE:
                maxPoints = CHUNK_SIZES.SAMPLE;
                samplingMethod = 'representativo';
                break;
            case LOADING_MODES.PARTIAL:
                maxPoints = CHUNK_SIZES.PARTIAL;
                samplingMethod = 'mixto';
                break;
            case LOADING_MODES.FULL:
                maxPoints = validPoints.length;//Math.min(CHUNK_SIZES.MAX_SAFE, validPoints.length);
                samplingMethod = 'completo (sin límites)';//validPoints.length > CHUNK_SIZES.MAX_SAFE ? 'limitado por seguridad' : 'completo';
                break;
            default:
                maxPoints = CHUNK_SIZES.SAMPLE;
                samplingMethod = 'representativo';
        }

        if (validPoints.length > maxPoints) {
            console.log(`🚨 Aplicando ${loadingMode} (${validPoints.length} → ${maxPoints} puntos)`);

            try {
                // ✅ ESTRATEGIA CORREGIDA: Sin recursión entre funciones
                if (loadingMode === LOADING_MODES.SAMPLE) {
                    // Muestra estadísticamente representativa (SIN clustering interno)
                    processedData = calculateRepresentativeSample(validPoints, maxPoints);
                    samplingMethod = 'estratificado por alerta';
                } else if (loadingMode === LOADING_MODES.PARTIAL) {
                    // ✅ CORRECCIÓN: Usar ambos métodos SIN llamadas cruzadas
                    const stratifiedSize = Math.floor(maxPoints * 0.7); // 70% estratificado
                    const geographicSize = maxPoints - stratifiedSize;   // 30% geográfico

                    // Obtener muestra estratificada
                    const baseSample = calculateRepresentativeSample(validPoints, stratifiedSize);

                    // Obtener puntos no usados para clustering geográfico
                    const usedIds = new Set(baseSample.map(p => p.id || `${p.latitud}-${p.longitud}`));
                    const unusedPoints = validPoints.filter(p =>
                        !usedIds.has(p.id || `${p.latitud}-${p.longitud}`)
                    );

                    // Aplicar clustering geográfico a puntos no usados
                    const geographicSample = geographicClustering(unusedPoints, geographicSize);

                    processedData = [...baseSample, ...geographicSample];
                    samplingMethod = 'estratificado + geográfico';
                } else {
                    processedData = validPoints;
                    samplingMethod = `completo (${validPoints.length.toLocaleString()} puntos)`;
                    console.log(`🌍 MODO FULL: Mostrando TODOS los puntos: ${validPoints.length}`);

                    // // ✅ MODO FULL: Sin clustering, corte simple
                    // if (validPoints.length > CHUNK_SIZES.MAX_SAFE) {
                    //     processedData = validPoints.slice(0, CHUNK_SIZES.MAX_SAFE);
                    //     samplingMethod = `primeros ${CHUNK_SIZES.MAX_SAFE.toLocaleString()} (limitado por seguridad)`;
                    //     console.log(`🔒 Limitado por seguridad: mostrando ${CHUNK_SIZES.MAX_SAFE} de ${validPoints.length} puntos`);
                    // } else {
                    //     processedData = validPoints;
                    //     samplingMethod = 'completo';
                    //     console.log(`✅ Mostrando todos los puntos: ${validPoints.length}`);
                    // }
                }
            } catch (error) {
                console.error("Error en muestreo avanzado, usando fallback simple:", error);
                // ✅ FALLBACK
                if (loadingMode === LOADING_MODES.FULL) {
                    // En modo FULL, incluso el fallback muestra todos los puntos
                    processedData = validPoints;
                    samplingMethod = 'completo (fallback)';
                } else {
                    processedData = getSystematicSample(validPoints, maxPoints);
                    samplingMethod = 'sistemático (fallback)';
                }
                // console.error("Error en muestreo avanzado, usando fallback simple:", error);
                // // ✅ FALLBACK SIMPLE Y SEGURO
                // processedData = getSystematicSample(validPoints, maxPoints);
                // samplingMethod = 'sistemático (fallback)';
            }

            optimized = processedData.length < validPoints.length;

            // Asegurar IDs únicos
            processedData = processedData.map((point, index) => ({
                ...point,
                id: point.id || `map-point-${index}-${Date.now()}`
            }));
        } else {
            // Si hay menos puntos que el límite, usar todos
            processedData = validPoints;
            samplingMethod = 'completo';
            optimized = false;
        }

        // Calcular estadísticas por tipo de alerta
        const alertStats = new Map();
        processedData.forEach(point => {
            const alertType = point.alerta || 'Sin clasificar';
            alertStats.set(alertType, (alertStats.get(alertType) || 0) + 1);
        });

        const loadingOptions = {
            canLoadMore: loadingMode === LOADING_MODES.SAMPLE && validPoints.length > CHUNK_SIZES.PARTIAL,
            canLoadFull: loadingMode !== LOADING_MODES.FULL && validPoints.length > CHUNK_SIZES.PARTIAL,
            totalAvailable: validPoints.length,
            currentShowing: processedData.length,
            showSampleInfo: optimized,
            nextLoadSize: loadingMode === LOADING_MODES.SAMPLE ? CHUNK_SIZES.PARTIAL : Math.min(CHUNK_SIZES.MAX_SAFE, validPoints.length)
        };

        return {
            optimizedData: processedData,
            isOptimized: optimized,
            stats: {
                original: data.length,
                valid: validPoints.length,
                final: processedData.length,
                optimized,
                mode: loadingMode,
                samplingMethod,
                alertTypes: alertStats.size,
                alertDistribution: Array.from(alertStats.entries())
            },
            loadingOptions
        };
    }, [data, loadingMode]);

    // 🚀 FUNCIÓN: Cargar más datos con progreso visual
    const handleLoadMore = async (targetMode) => {
        setIsLoadingMore(true);
        setLoadingProgress(0);

        // Simular progreso de carga realista
        const progressInterval = setInterval(() => {
            setLoadingProgress(prev => {
                if (prev < 30) return prev + 8;
                if (prev < 70) return prev + 5;
                if (prev < 90) return prev + 2;
                return Math.min(prev + 1, 95);
            });
        }, 100);

        // Tiempo de procesamiento basado en el tamaño del dataset
        const processingTime = data.length > 100000 ? 1500 : 800;

        await new Promise(resolve => setTimeout(resolve, processingTime));

        clearInterval(progressInterval);
        setLoadingProgress(100);

        setTimeout(() => {
            setLoadingMode(targetMode);
            setIsLoadingMore(false);
            setLoadingProgress(0);

            // Mostrar alert de cambio si es relevante
            if (targetMode === LOADING_MODES.FULL) {
                setShowOptimizationAlert(true);
            }
        }, 200);
    };

    // Detectar cuando los datos están listos para procesar
    useEffect(() => {
        if (data && data.length > 0 && loadingState === LOADING_STATES.INITIAL) {
            setLoadingState(LOADING_STATES.PROCESSING);

            // Tiempo de procesamiento basado en dataset y modo
            const baseTime = data.length > 100000 ? 1000 : 300;
            const modeMultiplier = loadingMode === LOADING_MODES.FULL ? 1.3 : 1;
            const processingTime = baseTime * modeMultiplier;

            setTimeout(() => {
                setLoadingState(LOADING_STATES.READY);
                if (isOptimized && loadingMode === LOADING_MODES.SAMPLE) {
                    setShowOptimizationAlert(true);
                }
            }, processingTime);
        }
    }, [data, loadingState, isOptimized, loadingMode]);

    // Marcar como renderizado cuando el mapa esté listo
    const handleMapReady = () => {
        setLoadingState(LOADING_STATES.RENDERED);
        console.log(`✅ Mapa renderizado: ${stats.final} puntos de ${stats.original} originales (${stats.samplingMethod})`);
    };

    // Verificar si hay datos válidos
    if (!Array.isArray(data) || data.length === 0) {
        return (
            <Card className="h-100">
                <FalconCardHeader
                    title="Ubicación de alertas"
                    titleTag="h6"
                    light={true}
                />
                <Card.Body className="h-100 p-0 d-flex justify-content-center align-items-center">
                    <div className="text-center text-muted">
                        <i className="fas fa-map-marker-alt fa-3x mb-3 opacity-50"></i>
                        <p className="mb-0">No hay datos de ubicación disponibles</p>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    // Estado de carga inicial y procesamiento
    if (loadingState === LOADING_STATES.INITIAL || loadingState === LOADING_STATES.PROCESSING) {
        return (
            <Card className="h-100">
                <FalconCardHeader
                    title="Ubicación de alertas"
                    titleTag="h6"
                    light={true}
                />
                <Card.Body className="h-100 p-0 d-flex justify-content-center align-items-center">
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mb-0 mt-2">
                            {loadingState === LOADING_STATES.PROCESSING
                                ? `Optimizando ${data.length.toLocaleString()} puntos (${loadingMode})...`
                                : 'Inicializando mapa...'}
                        </p>
                        {loadingState === LOADING_STATES.PROCESSING && (
                            <small className="text-muted">
                                Aplicando muestreo {stats.samplingMethod || 'inteligente'}
                            </small>
                        )}
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="h-100">
            <FalconCardHeader
                className='p-3'
                title={
                    <div className="d-flex align-items-center">
                        <span>
                            Ubicación de alertas ({stats.final.toLocaleString()}
                            {isOptimized ? ` de ${stats.original.toLocaleString()}` : ''})
                        </span>
                        {/*stats.alertTypes > 1 && (
                            <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7em' }}>
                                {stats.alertTypes} tipos
                            </Badge>
                        )*/}

                        {/*loadingMode === LOADING_MODES.FULL && !isOptimized && (
                            <Badge bg="success" className="ms-2" style={{ fontSize: '0.7em' }}>
                                COMPLETO
                            </Badge>
                        )*/}

                    </div>
                }
                titleTag="h6"
                light={true}
            />

            {/* Alerta de optimización mejorada con detalles */}
            {showOptimizationAlert && (
                <Alert
                    variant={loadingMode === LOADING_MODES.SAMPLE ? "info" :
                        loadingMode === LOADING_MODES.PARTIAL ? "warning" : "success"}
                    dismissible
                    onClose={() => setShowOptimizationAlert(false)}
                    className="mx-3 mt-2 mb-0"
                >
                    <Row className="align-items-center">
                        <Col>
                            <div className="d-flex align-items-center">
                                <i className={
                                    loadingMode === LOADING_MODES.SAMPLE ? "fas fa-chart-pie me-2" :
                                        loadingMode === LOADING_MODES.PARTIAL ? "fas fa-layer-group me-2" :
                                            "fas fa-globe me-2"
                                }></i>
                                <div>
                                    <strong>
                                        {loadingMode === LOADING_MODES.SAMPLE && 'Muestra representativa:'}
                                        {loadingMode === LOADING_MODES.PARTIAL && 'Carga parcial optimizada:'}
                                        {loadingMode === LOADING_MODES.FULL && 'Vista completa:'}
                                    </strong>
                                    <span className="ms-1">
                                        {stats.final.toLocaleString()} puntos de {stats.original.toLocaleString()} totales
                                    </span>
                                    {/* <br />
                                    <small className="text-muted">
                                        Método: {stats.samplingMethod} • {stats.alertTypes} tipos de alerta
                                        {loadingMode === LOADING_MODES.FULL && !isOptimized &&
                                            " • Mostrando dataset completo"}
                                    </small> */}
                                    {/* <small className="text-muted">
                                        Método: {stats.samplingMethod} • {stats.alertTypes} tipos de alerta
                                        {stats.mode === LOADING_MODES.FULL && stats.final === CHUNK_SIZES.MAX_SAFE && 
                                            " • Límite de seguridad aplicado"}
                                    </small> */}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Alert>
            )}

            {/* Barra de progreso para carga adicional */}
            {/* {isLoadingMore && (
                <div className="mx-3 mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-primary">
                            <i className="fas fa-cog fa-spin me-1"></i>
                            Procesando datos adicionales...
                        </small>
                        <small className="text-primary font-weight-bold">{loadingProgress}%</small>
                    </div>
                    <ProgressBar 
                        now={loadingProgress} 
                        style={{ height: '6px' }}
                        className="progress-bar-animated"
                        variant="primary"
                    />
                </div>
            )} */}

            {loadingMode === LOADING_MODES.FULL && stats.final > 10000 && showLargeDatasetAlert && (
                <Alert
                    variant="warning"
                    className="mx-3 mt-2 mb-0"
                    dismissible
                    onClose={() => setShowLargeDatasetAlert(false)}
                >
                    <div className="d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <div className="flex-grow-1">
                            <small>
                                <strong>Dataset grande:</strong> {stats.final.toLocaleString()} puntos cargados.
                                El rendimiento del mapa puede verse afectado con esta cantidad de datos.
                            </small>
                        </div>
                    </div>
                </Alert>
            )}

            <Card.Body className="h-100 p-0" dir="ltr">
                <Suspense fallback={
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" size="sm" />
                            <p className="mb-0 mt-2">Cargando componente de mapa...</p>
                        </div>
                    </div>
                }>
                    <LeafletMap
                        data={optimizedData} // ✅ Usar datos optimizados
                        className="h-100 bg-body-tertiary"
                        style={{ minHeight: '300px' }}
                        isGroup={isGroup}
                        onMapReady={handleMapReady}
                    />
                </Suspense>
            </Card.Body>

            <Card.Footer className="bg-body-tertiary">
                <Row className='d-flex'>
                    <Col className='justify-content-end align-items-end text-end'>

                        {/* Nuevo Footer */}
                        {loadingOptions.canLoadFull && !isLoadingMore && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleLoadMore(LOADING_MODES.FULL)}
                                title={`Cargar TODOS los ${loadingOptions.totalAvailable.toLocaleString()} puntos disponibles`}
                            >
                                <i className="fas fa-globe me-1"></i>
                                {/* ✅ TEXTO ACTUALIZADO para claridad */}
                                Todas ({loadingOptions.totalAvailable.toLocaleString()})
                            </Button>
                        )}
                        {isLoadingMore && (
                            <Button variant="secondary" size="sm" disabled>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Cargando...
                            </Button>
                        )}


                        {/* <div className="d-flex align-items-center">

                            {stats.mode === LOADING_MODES.SAMPLE && (
                                <i className="fas fa-chart-pie text-info me-2" title="Muestra representativa"></i>
                            )}
                            {stats.mode === LOADING_MODES.PARTIAL && (
                                <i className="fas fa-layer-group text-warning me-2" title="Carga parcial"></i>
                            )}
                            {stats.mode === LOADING_MODES.FULL && (
                                <i className="fas fa-globe text-success me-2" title="Vista completa"></i>
                            )}

                            <small className="text-muted">
                                <strong>{stats.final.toLocaleString()}</strong> de{' '}
                                <strong>{stats.original.toLocaleString()}</strong> puntos
                                {stats.alertTypes > 1 && (
                                    <span className="ms-1">• {stats.alertTypes} tipos de alerta</span>
                                )}

                                {stats.mode === LOADING_MODES.FULL && !isOptimized && (
                                    <span className="text-success ms-1">• Dataset completo</span>
                                )}
                            </small>

                        </div> */}

                        {/* Distribución de alertas (solo para muestra) */}
                        {/* {stats.mode === LOADING_MODES.SAMPLE && stats.alertDistribution.length > 1 && (
                            <div className="mt-1">
                                <small className="text-muted">
                                    Distribución: {stats.alertDistribution.slice(0, 3).map(([type, count]) => 
                                        `${type}(${count})`
                                    ).join(', ')}
                                    {stats.alertDistribution.length > 3 && '...'}
                                </small>
                            </div>
                        )} */}
                    </Col>


                    {/* <Col xs="auto">
                        <div className="d-flex align-items-center gap-2">
                           
                            {loadingState === LOADING_STATES.RENDERED && (
                                <small className="text-success">
                                    <i className="fas fa-check-circle me-1"></i>
                                    Mapa cargado
                                </small>
                            )}

                          
                            {isOptimized && !isLoadingMore && (
                                <Badge
                                    bg="light"
                                    text="dark"
                                    className="text-uppercase"
                                    style={{ fontSize: '0.65em' }}
                                    title={`Método de muestreo: ${stats.samplingMethod}`}
                                >
                                    {stats.samplingMethod.split(' ')[0]}
                                </Badge>
                            )}
                          
                            {!isOptimized && loadingMode === LOADING_MODES.FULL && (
                                <Badge
                                    bg="success"
                                    className="text-uppercase"
                                    style={{ fontSize: '0.65em' }}
                                    title="Dataset completo sin optimizaciones"
                                >
                                    COMPLETO
                                </Badge>
                            )}
                        </div>
                    </Col> */}


                </Row>
            </Card.Footer>

        </Card>
    );
};

AlertaUbicacion.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            latitud: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            longitud: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            alerta: PropTypes.string,
            direccion: PropTypes.string,
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    ),
    isGroup: PropTypes.bool
};

export default React.memo(AlertaUbicacion);







// import React from 'react';
// import PropTypes from 'prop-types';
// import { Card, Col, Row } from 'react-bootstrap';
// import FalconCardHeader from '../../../../../components/common/FalconCardHeader';
// import LeafletMap from './LeafletMap';

// const AlertaUbicacion = ({ data, isGroup }) => {
//     // Verificar si hay datos
//     if (!Array.isArray(data) || data.length === 0) {
//         return (
//             <Card className="h-100">
//                 <FalconCardHeader
//                     title="Ubicación de alertas"
//                     titleTag="h6"
//                     light={true}
//                 />
//                 <Card.Body className="h-100 p-0 d-flex justify-content-center align-items-center">
//                     <p className="text-center mt-3">No hay datos de ubicación disponibles</p>
//                 </Card.Body>
//             </Card>
//         );
//     }

//     return (
//         <Card className="h-100">
//             <FalconCardHeader
//                 title={`Ubicación de alertas (${data.length})`}
//                 titleTag="h6"
//                 light={true}
//             />
//             <Card.Body className="h-100 p-0" dir="ltr">
//                 <LeafletMap
//                     data={data}
//                     className="h-100 bg-body-tertiary"
//                     style={{ minHeight: '300px' }}
//                     isGroup={isGroup}
//                 />
//             </Card.Body>
//             {/* <Card.Footer className="bg-body-tertiary">
//                 <Row className="justify-content-between">
//                     <Col xs="auto">
//                         {data.length >= 1000 && (
//                             <small className="text-muted">
//                                 Mostrando primeros 1000 puntos para mejor rendimiento
//                             </small>
//                         )}
//                     </Col>
//                 </Row>
//             </Card.Footer> */}
//         </Card>
//     );
// };

// AlertaUbicacion.propTypes = {
//     data: PropTypes.arrayOf(
//         PropTypes.shape({
//             latitud: PropTypes.number,
//             longitud: PropTypes.number,
//             alerta: PropTypes.string,
//             direccion: PropTypes.string
//         })
//     ),
//     isGroup: PropTypes.bool
// };

// export default AlertaUbicacion;